import { useAccount, useIsAuthenticated, useMsal } from '@azure/msal-react'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import ChatInterface from '@components/ChatInterface'
import Dashboard from '@components/Dashboard'
import Notification from '@components/Notification'
import Sidebar from '@components/Sidebar'
import UserMenu from '@components/UserMenu'

import { useAccentColor } from '@hooks/useAccentColor'
import useDify from '@hooks/useDify'
import difyService from '@services/dify.service'

import type { FileUploadResponse } from '@typings/agent'
import type { Agent, Conversation, FileRecord, Message } from '@typings/dify'

import useThemeSync from '@hooks/useThemeSync'
import Login from './Login'
import { fetchAvailableAgents } from '@services/agent.service'
import { isAdmin } from '@config/auth.config'
import { setCurrentAccount } from '@services/api.service'
import Loader from '@components/Loader'
import { useTeams } from '@contexts/TeamsContext'
import { useTeamsAuth } from '@hooks/useTeamsAuth'

const Home = () => {
  const [sidebarOpen, setSidebarOpen] = useState(true)
  const [selectedAgent, setSelectedAgent] = useState<Agent | null>(null)
  const [selectedConversation, setSelectedConversation] = useState<Conversation | null>(null)
  const [messages, setMessages] = useState<Message[]>([])
  const { theme, setTheme, effectiveTheme } = useThemeSync()
  const [showDashboard, setShowDashboard] = useState(false)
  const [isAnswering, setIsAnswering] = useState(false)
  const [uploadedFile, setUploadedFile] = useState<FileUploadResponse[]>([])
  const [queuedFiles, setQueuedFiles] = useState<File[]>([])
  const [prevAgents, setPrevAgents] = useState<Agent[]>([])
  const [currentTaskId, setCurrentTaskId] = useState<string | null>(null)
  const [isUserAdmin, setIsUserAdmin] = useState(false);

  const { t } = useTranslation()
  const isAuthenticated = useIsAuthenticated()
  const { accounts } = useMsal()
  const account = useAccount(accounts[0] || {})
  const [accentColor, setAccentColor] = useAccentColor();

  const [agents, setAgents] = useState<Agent[]>([])
  const [loading, setLoading] = useState(true)

  // Support Teams
  const { isInTeams } = useTeams()
  const { getTeamsToken } = useTeamsAuth()

  const {
    error,
    setError,
    sendMessage,
    newConversation,
    deleteConversation,
    toggleFavoriteConversation,
    renameConversation,
    likeMessage,
    reorderAgents,
    favoriteAgents,
    uploadFile,
  } = useDify(agents, setAgents);

  const initializeUser = async () => {
    try {
      setCurrentAccount(account);

      // Vérifier si l'utilisateur est admin
      if (account) {
        const adminStatus = await isAdmin(account);
        
        setIsUserAdmin(adminStatus);
      }
    } catch (error) {
      console.error("Error fetching token:", error);
    }
  }

  const fetchData = async () => {
    const result = await fetchAvailableAgents()

    const defaultAgent: Agent = {
      id: 0,
      isActive: false,
      apiKey: '',
      color: '',
      isFavorite: false,
      supportsFileUpload: false,
      messageCount: 0,
      likeCount: 0,
      lastUsed: new Date(),
      idGroupEntra: [],
      translations: [],
      conversations: [],
      stats: {
        messageCount: 0,
        likeCount: 0,
        lastUsed: ''
      }
    }

    const validatedAgents = result.map((agent: Partial<Agent>) => ({
      ...defaultAgent,
      ...agent,
      stats: {
        ...defaultAgent.stats,
        ...agent.stats
      }
    }))

    setAgents(validatedAgents)
  }

  // Effet pour l'authentification Teams SSO
  useEffect(() => {
    const initTeamsAuth = async () => {
      if (isInTeams && !isAuthenticated) {
        // Tenter l'authentification Teams SSO
        const token = await getTeamsToken()
        if (token) {
          console.log('Teams SSO authentication successful')
        }
      }
    }
    initTeamsAuth()
  }, [isInTeams, isAuthenticated, getTeamsToken])

  useEffect(() => {
    const init = async () => {
      if (account) {
        setCurrentAccount(account);
        await initializeUser();
        setLoading(true);
        await fetchData();
      }
      setLoading(false);
    };
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [account]);

  //Charger les conversations pour chaque agent après avoir chargé les agents
  const isConversationsLoaded = useRef(false);

  useEffect(() => {
    if (!agents.length || isConversationsLoaded.current) return;
      const loadConversations = async () => {
        const data = await Promise.all(
          agents.map(async (agent) => {
            const conversations = await difyService.getConversations(agent);
            return { ...agent, conversations };
          })
        );
        setAgents(data);
        isConversationsLoaded.current = true;
      }
      loadConversations();
  }, [agents, setAgents]);

  //Charger les messages, pour chaque conversation après avoir chargé les conversations
  const isMessagesLoaded = useRef(false);

  useEffect(() => {
    if (!isConversationsLoaded.current || isMessagesLoaded.current) return;
    const loadMessages = async () => {
      const data = await Promise.all(
        agents.map(async (agent) => {
          const conversations = await Promise.all(
            agent.conversations.map(async (conversation) => {
              const messages = await difyService.getConversationHistory(agent, conversation.id);

              // Récupérer les fichiers liés à la conversation à partir des messages envoyés par l'utilisateur (inputs.Document)
              const conversationFiles: FileRecord[] = [];
              const relatedIds = new Set<string>(); // Pour éviter les doublons
              // Parcourir les messages pour extraire les fichiers liés
              if (messages && messages.length > 0) {
                messages.forEach(message => {
                  if (message.role === 'user' && message.inputs?.Document && Array.isArray(message.inputs.Document)) {
                    message.inputs.Document.forEach((file) => {
                      if (file.related_id && !relatedIds.has(file.related_id)) {
                        relatedIds.add(file.related_id);
                        // Ajouter le fichier à la liste des fichiers de la conversation
                        conversationFiles.push({
                          id: file.related_id,
                          filename: file.filename,
                          size: file.size,
                          extension: file.extension,
                          mime_type: file.mime_type,
                          url: file.remote_url || ''
                        });
                      }
                    });
                  }
                });
              }
              // Préserver les fichiers existants de la conversation locale
              const existingConversation = agents.find(a => a.id === agent.id)
                ?.conversations.find(c => c.id === conversation.id);
              if (existingConversation?.files && existingConversation.files.length > 0) {
                existingConversation.files.forEach(file => {
                  if (!relatedIds.has(file.id)) {
                    relatedIds.add(file.id);
                    conversationFiles.push(file);
                  }
                });
              }
              //Statistiques likes et messages envoyés
              agent.stats = agent.stats || {};
              agent.stats.messageCount = (agent.stats.messageCount || 0) + (messages ? messages.length / 2 : 0); //On divise par 2 pour n'obtenir que le nombre de message envoyés
              agent.stats.likeCount = (agent.stats.likeCount || 0) + (messages ? messages.filter((message: Message) => message.liked).length : 0);
              
              return { ...conversation, messages, files: conversationFiles };
            })
          );
          return { ...agent, conversations};
        })
      );
      setAgents(data);
      isMessagesLoaded.current = true;
    };
    loadMessages();
  }, [agents, setAgents]);

  const ensureValidAccentColor = () => {
    if (!['lightBlue', 'navyBlue', 'green', 'yellow', 'limeGreen'].includes(accentColor)) {
      setAccentColor('lightBlue')
    }
  }

  // Met à jour les messages lorsque la conversation sélectionnée change
  const updateConversationMessages = () => {
    if (selectedConversation) {
      setMessages(selectedConversation.messages)
    }
  }

  const reloadAgentData = () => {
    if (agents !== prevAgents) {
      const agent = agents.find((a) => a.id === selectedAgent?.id)
      setSelectedAgent(agent || null)

      const conversation =
        agent?.conversations.find((c) => c.id === selectedConversation?.id) || agent?.conversations[0]
      if (conversation) {
        setSelectedConversation(conversation)
        setMessages(conversation.messages)
      }
      setPrevAgents(agents)
    }
  }

  const handleNewConversation = async () => {
    if (selectedAgent) {
      const newConv = newConversation(selectedAgent)

      if (newConv) {
        setSelectedConversation(newConv)
        setMessages([])

        const initialMessage = t('chat.initial_message') || "Décris moi ce que tu peux faire/Tes possibilités";

        try {
          await sendMessage(
            selectedAgent,
            initialMessage,
            newConv,
            'streaming',
            setSelectedConversation,
            setMessages,
            setIsAnswering,
            undefined,
            (taskId) => setCurrentTaskId(taskId)
          );
        } catch (error) {
          console.error('Failed to send initial message:', error);
        } finally {
          setCurrentTaskId(null);
          setIsAnswering(false);
        }
      }
    }
  }

  const handleStopGenerating = async () => {
    if (selectedAgent && currentTaskId) {
      try {
        await difyService.stopGenerating(selectedAgent, currentTaskId);
        setIsAnswering(false);
        setCurrentTaskId(null);
      } catch (error) {
        console.error('Failed to stop generation:', error);
        setError('Failed to stop generation');
      }
    }
  };

  const handleSendMessage = async (content: string) => {
    if (selectedAgent) {
      try {
        await sendMessage(
          selectedAgent,
          content,
          selectedConversation || null,
          'streaming',
          setSelectedConversation,
          setMessages,
          setIsAnswering,
          uploadedFile ? uploadedFile : undefined,
          (taskId) => setCurrentTaskId(taskId)
        );
      } catch (error) {
        console.error('Failed to send message:', error);
      } finally {
        setCurrentTaskId(null);
        setIsAnswering(false);
      }
    }
  };

  const handleReorderAgents = (reorderedAgents: Agent[]) => {
    reorderAgents(reorderedAgents)
  }

  const handleLikeMessage = async (messageId: string) => {
    if (selectedAgent && selectedConversation) {
      likeMessage(selectedAgent, selectedConversation.id, messageId)
    }
  }

  const handleToggleFavorite = (agentId: string) => {
    favoriteAgents(agentId)
  }

  const handleToggleConversationFavorite = (conversationId: string) => {
    if (selectedAgent) {
      toggleFavoriteConversation(selectedAgent, conversationId)
    }
  }

  const handleRenameConversation = (conversationId: string, newTitle: string) => {
    if (selectedAgent) {
      const conversation = selectedAgent.conversations.find((conv) => conv.id === conversationId)
      if (conversation) {
        const updatedTitle = conversation.isFavorite ? `[Favorite] ${newTitle}` : newTitle
        renameConversation(selectedAgent, conversationId, updatedTitle)
      }
    }
  }

  const handleDeleteConversation = async (conversationId: string) => {
    if (selectedAgent) {
      deleteConversation(selectedAgent, conversationId)
      setSelectedConversation(null)
      setMessages([])
    }
  }

  const handleConversationSelection = (conversation: Conversation) => {
    if (conversation.id === selectedConversation?.id) return
    if (isAnswering) return
    const updatedMessages = conversation.messages.map((message) => {
      if (message.isNew) {
        delete message.isNew
      }
      return message
    })

    conversation.messages = updatedMessages
    setSelectedConversation(conversation)
  }

 // Gestion du téléversement de fichiers appelléee depuis ChatInterface (onFileUpload)
 // Utilise un ref pour suivre les fichiers déjà traités par conversation, afin d'éviter les retéléversements/doubles requêtes
  const processedFilesByConversation = useRef<Record<string, Set<string>>>({});

  const handleFileUpload = async (files?: File[]) => {
    if (!selectedAgent || !selectedConversation) return

    const incomingFiles = Array.isArray(files) ? files : Array.from(files || [])

    if (!incomingFiles.length) {
      if (queuedFiles.length || uploadedFile.length) {
        setQueuedFiles([])
        setUploadedFile([])
      }
      return
    }

    //Initialise le set des fichiers de la conversation si nécessaire
    if (!processedFilesByConversation.current[selectedConversation.id]) {
    processedFilesByConversation.current[selectedConversation.id] = new Set();
    }
    const processedFiles = processedFilesByConversation.current[selectedConversation.id];

    //Filtre les fichiers déjà traités et ignore les doublons
    const newFiles = incomingFiles.filter((file) => {
      const fileKey = `${file.name}-${file.lastModified}`;
      if (processedFiles.has(fileKey)) {
        return false;
      }
      processedFiles.add(fileKey);
      return true;
    });
    if (!newFiles.length) return;

    //Fichier supprimé de la liste des fichiers à uploader
    const removedFiles = queuedFiles.filter((existing) => !incomingFiles.includes(existing))
    //Supprimer les fichiers supprimés de la liste des fichiers uploadés
    if (removedFiles.length) {
      setUploadedFile((prev) =>
        prev.filter(
          (uploaded) =>
            !removedFiles.some(
              (removed) => removed.name === uploaded.name && removed.size === uploaded.size,
            ),
        ),
      )
    }
    //Nouveaux fichiers à uploader (évite de retéléverser les fichiers déjà uploadés)
    const addedFiles = incomingFiles.filter((file) => !queuedFiles.includes(file))
    setQueuedFiles(incomingFiles)

    if (!addedFiles.length) {
      return
    }
    try {
      //Upload de chaque nouveaux fichiers
      const uploaded = await Promise.all(addedFiles.map((file) => uploadFile(selectedAgent, file)))

      //Conserve uniquement les uploads réussis
      const success = uploaded.filter(Boolean) as FileUploadResponse[]
      //Ajoute les nouveaux fichiers uploadés à la liste des fichiers uploadés
      if (success.length) {
        setUploadedFile((prev) => [...prev, ...success])
      }
    } catch (error) {
      console.error('Failed to upload files:', error)
      setError('Failed to upload files')
    }
  }
  
  useEffect(ensureValidAccentColor, [accentColor, setAccentColor])
  useEffect(updateConversationMessages, [selectedConversation])
  useEffect(reloadAgentData, [agents, prevAgents, selectedAgent?.id, selectedConversation])

  return (
    <>
      {loading ? (
        <div className="h-screen flex bg-gray-50 dark:bg-gray-900 transition-colors">
          <div className="flex-1 flex items-center justify-center">
            <Loader />
          </div>
        </div>
      ) : isAuthenticated ? (
        <div className="h-screen flex bg-gray-50 dark:bg-gray-900 transition-colors">
          <Sidebar
            theme={effectiveTheme}
            isOpen={sidebarOpen}
            setIsOpen={setSidebarOpen}
            agents={agents}
            selectedAgent={selectedAgent}
            onSelectAgent={(agent) => {
              if (agent.id === selectedAgent?.id) return
              if (isAnswering) return

              setSelectedAgent(agent)
              setSelectedConversation(null)
              setMessages([])
            }}
            onToggleFavorite={handleToggleFavorite}
            onReorderAgents={handleReorderAgents}
            accentColor={accentColor}
          />

          <div className={`flex-1 flex flex-col transition-all ${sidebarOpen ? 'ml-72' : 'ml-16'}`}>
            <div className="h-16 border-b dark:border-gray-700 flex items-center justify-between px-4">
              <div className="flex items-center space-x-2">
                <h1 className="text-2xl font-bold dark:text-white ml-4">
                  {selectedAgent ? (
                    <>
                      { selectedAgent?.translations?.find(
                          (translation) => translation.field === 'label' && translation.language.toLowerCase() === localStorage.getItem('i18nextLng')?.toLowerCase()
                        )?.value 
                      } &nbsp;
                      <small className="text-xs weight-bold text-gray-500 dark:text-gray-400 font-normal">
                        { selectedAgent?.translations?.find(
                          (translation) => translation.field === 'description' && translation.language.toLowerCase() === localStorage.getItem('i18nextLng')?.toLowerCase()
                        )?.value  
                        }
                      </small>
                    </>
                  ) : (
                    t('home.select_agent')
                  )}
                </h1>
              </div>
              <UserMenu
                theme={theme}
                setTheme={setTheme}
                onShowDashboard={() => setShowDashboard(true)}
                account={account}
                accentColor={accentColor}
                setAccentColor={setAccentColor}
                agents={agents}
                showAgentsManagement={isUserAdmin}
              />
            </div>

            <div className="flex-1 overflow-hidden">
              <Dashboard
                agents={agents}
                accentColor={accentColor}
                isOpen={showDashboard}
                onClose={() => setShowDashboard(false)}
              />

              <ChatInterface
                selectedAgent={selectedAgent}
                messages={messages}
                onSendMessage={handleSendMessage}
                onLikeMessage={handleLikeMessage}
                selectedConversation={selectedConversation}
                onSelectConversation={handleConversationSelection}
                onDeleteConversation={handleDeleteConversation}
                onToggleFavorite={handleToggleConversationFavorite}
                onRenameConversation={handleRenameConversation}
                onNewConversation={handleNewConversation}
                onFileUpload={handleFileUpload}
                onStopGenerating={handleStopGenerating}
                accentColor={accentColor}
                isAnswering={isAnswering}
                onError={setError}
              />
            </div>
          </div>
          <Notification message={error} onClose={() => setError(null)} />
        </div>
      ) : (
        <Login />
      )}
    </>
  )
}

export default Home
